<template>
    <div class="ai-page">
        <!-- Header -->
        <div class="ai-header">
            <div class="ai-header-left">
                <div class="ai-icon-wrap"><i class="fas fa-file-import"></i></div>
                <div>
                    <h2 class="ai-title">Import Attendance</h2>
                    <span class="ai-subtitle">Upload valid CSV files only</span>
                </div>
            </div>
            <a class="ai-download-btn" :href="getAssetUrlPath('/uploads/excel/Import attendance format.csv')" target="_blank" download="Import attendance format">
                <i class="fas fa-download"></i> Download Template
            </a>
        </div>

        <!-- Info Note -->
        <div class="ai-note">
            <div class="ai-note-icon"><i class="fas fa-info-circle"></i></div>
            <p class="ai-note-text">
                Employee code, status (A, WO, L, etc), and department must exist in the database.
                The month selected below will be used regardless of dates in CSV.
                Existing records for that date will be <strong>overwritten</strong>.
            </p>
        </div>

        <!-- Form Card -->
        <div class="ai-form-card">
            <h3 class="ai-form-title"><i class="fas fa-cog"></i> Import Settings</h3>
            <div class="ai-form-grid">
                <div class="ai-field">
                    <label class="ai-label">Year <span class="ai-req">*</span></label>
                    <input type="number" class="ai-input" v-model="year" />
                </div>
                <div class="ai-field">
                    <label class="ai-label">Month <span class="ai-req">*</span></label>
                    <select v-model="month" class="ai-input ai-select">
                        <option v-for="m in months" :key="m.val" :value="m.val">{{ m.name }}</option>
                    </select>
                </div>
                <div class="ai-field">
                    <label class="ai-label">CSV File <span class="ai-req">*</span></label>
                    <div class="ai-file-wrap">
                        <label class="ai-file-btn" :class="{ 'ai-file-selected': csvFile }">
                            <i :class="csvFile ? 'fas fa-check-circle' : 'fas fa-cloud-upload-alt'"></i>
                            {{ csvFile ? csvFile.name : 'Choose file...' }}
                            <input type="file" accept="text/csv" class="ai-file-input" @click="$event.target.value = null" @change="handleSelectCsvFile" />
                        </label>
                    </div>
                </div>
            </div>
            <button class="ai-import-btn" @click="verifyImport" :disabled="processing">
                <i :class="processing ? 'fas fa-spinner fa-spin' : 'fas fa-upload'"></i>
                {{ processing ? 'Processing...' : 'Import Attendance' }}
            </button>
        </div>

        <!-- Import Result Modal -->
        <ImportErrorModal
            :show="showImportResult"
            :importStatus="importStatus"
            :importSuccess="importSuccess"
            :importErrors="importErrors"
            :processing="processing"
            @close="handleImportModalClose"
        >
            <a href="/employees" class="ai-view-link"><i class="fas fa-users"></i> View Employees</a>
        </ImportErrorModal>

        <!-- Warning Modal -->
        <CustomModal v-if="showWarningModal" title="Warning" :show="showWarningModal" @close="showWarningModal = false">
            <div class="ai-warning-body">
                <div class="ai-warning-icon"><i class="fas fa-exclamation-triangle"></i></div>
                <p class="ai-warning-text">
                    Matching attendance records found for <strong>{{ getMonthName(month) }}, {{ year }}</strong>.
                    Existing data will be <strong>overwritten</strong>. Continue?
                </p>
                <div class="ai-warning-actions">
                    <button class="ai-cancel-btn" @click="showWarningModal = false">Cancel</button>
                    <button class="ai-confirm-btn" @click="handleImport" :disabled="processing">
                        <i :class="processing ? 'fas fa-spinner fa-spin' : 'fas fa-check'"></i>
                        {{ processing ? 'Importing...' : 'Confirm Import' }}
                    </button>
                </div>
            </div>
        </CustomModal>
    </div>
</template>

<script>
import { http } from "@/utils/http"
import { errMsg, getAssetUrlPath, getMonthName } from "../../utils/common"
import ImportErrorModal from "../../components/other/ImportErrorModal.vue"
import CustomModal from "../../components/layout/CustomModal.vue"

export default {
    components: { CustomModal, ImportErrorModal },
    data() {
        return {
            csvFile: null,
            year: new Date().getFullYear(),
            month: new Date().getMonth() + 1,
            processing: false,
            showImportResult: false,
            importStatus: "Pending",
            importErrors: null,
            importSuccess: false,
            showWarningModal: false,
            months: [
                { val: 1, name: 'January' }, { val: 2, name: 'February' }, { val: 3, name: 'March' },
                { val: 4, name: 'April' }, { val: 5, name: 'May' }, { val: 6, name: 'June' },
                { val: 7, name: 'July' }, { val: 8, name: 'August' }, { val: 9, name: 'September' },
                { val: 10, name: 'October' }, { val: 11, name: 'November' }, { val: 12, name: 'December' },
            ],
        }
    },
    methods: {
        getAssetUrlPath,
        getMonthName,
        handleImportModalClose() {
            if (this.importSuccess) { window.location.reload() } else { this.showImportResult = false }
        },
        async verifyImport() {
            if (!this.csvFile) return this.$toast.error("Please select a CSV file!")
            if (!this.year || !this.month) return this.$toast.error("Please select year and month!")
            try {
                this.processing = true
                const res = await http.get(`/attendance-records/count?year=${this.year}&month=${this.month}`)
                if (res.data.result > 0) { this.showWarningModal = true } else { this.processing = false; await this.handleImport() }
            } catch (err) { console.log("error in processing", err) }
            this.processing = false
        },
        async handleImport() {
            const formData = new FormData()
            if (this.processing) return
            formData.append("year", this.year)
            formData.append("month", this.month)
            formData.append("csvFile", this.csvFile)
            this.showWarningModal = false
            this.processing = true
            this.importSuccess = false
            this.showImportResult = true
            try {
                this.importStatus = "Importing..."
                await http.post("/attendance-records/import", formData)
                this.$toast.success("Imported Successfully!")
                this.importStatus = "Success"
                this.importSuccess = true
            } catch (err) {
                console.log("error in importing records", err)
                this.importErrors = err.response.data.result
                this.$toast.error(errMsg(err, "Could not import!"))
                this.importStatus = "Failed"
            }
            this.processing = false
        },
        handleSelectCsvFile(e) { this.csvFile = e.target.files[0] },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";
.ai-page { max-width:720px; animation: aiFade .3s ease; }
@keyframes aiFade { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }

/* Header */
.ai-header { display:flex; align-items:center; justify-content:space-between; gap:12px; background:white; border:1px solid $gray100; border-radius:8px; padding:10px 16px; margin-bottom:12px; box-shadow:$shadowSm; flex-wrap:wrap; }
.ai-header-left { display:flex; align-items:center; gap:10px; }
.ai-icon-wrap { width:32px; height:32px; border-radius:8px; background:linear-gradient(135deg,$primaryLight,darken($primaryLight,4%)); color:$primaryColor; display:flex; align-items:center; justify-content:center; font-size:14px; }
.ai-title { font-size:15px; font-weight:700; color:$gray800; margin:0; line-height:1.2; }
.ai-subtitle { font-size:11.5px; color:$gray400; font-weight:500; }
.ai-download-btn { display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border:1px solid $gray200; border-radius:6px; background:white; color:$primaryColor; font-size:12.5px; font-weight:600; text-decoration:none; transition:all $transitionFast; i { font-size:12px; } &:hover { background:$primaryLight; border-color:rgba(0,94,137,0.15); } }

/* Note */
.ai-note { display:flex; gap:10px; background:#fffbeb; border:1px solid #fde68a; border-radius:8px; padding:12px 16px; margin-bottom:12px; }
.ai-note-icon { width:20px; color:#d97706; font-size:14px; flex-shrink:0; margin-top:1px; }
.ai-note-text { font-size:12.5px; color:#92400e; line-height:1.5; margin:0; strong { font-weight:700; } }

/* Form Card */
.ai-form-card { background:white; border:1px solid $gray100; border-radius:10px; padding:20px; box-shadow:$shadowSm; }
.ai-form-title { font-size:13px; font-weight:700; color:$gray800; margin:0 0 16px; display:flex; align-items:center; gap:6px; i { font-size:12px; color:$primaryColor; } }
.ai-form-grid { display:grid; grid-template-columns:1fr 1fr; gap:14px; margin-bottom:16px; }
.ai-field { display:flex; flex-direction:column; gap:5px; &:last-child { grid-column:1/-1; } }
.ai-label { font-size:12px; font-weight:600; color:$gray600; }
.ai-req { color:#dc2626; }
.ai-input { width:100%; padding:9px 12px; border:1px solid $gray200; border-radius:6px; font-size:13px; font-weight:500; color:$gray800; background:white; transition:all $transitionFast; font-family:inherit; outline:none; &::placeholder { color:$gray300; } &:focus { border-color:$primaryColor; box-shadow:0 0 0 3px rgba(0,94,137,0.08); } }
.ai-select { cursor:pointer; }

/* File input */
.ai-file-wrap { position:relative; }
.ai-file-btn { display:flex; align-items:center; gap:8px; padding:10px 14px; border:2px dashed $gray200; border-radius:8px; font-size:12.5px; font-weight:600; color:$gray500; cursor:pointer; transition:all $transitionFast; &:hover { border-color:$primaryColor; color:$primaryColor; background:rgba(0,94,137,0.02); } i { font-size:14px; } }
.ai-file-selected { border-color:rgba(22,163,74,0.3); color:#16a34a; background:#f0fdf4; }
.ai-file-input { display:none; }

/* Import button */
.ai-import-btn { display:inline-flex; align-items:center; gap:6px; padding:9px 20px; border:none; border-radius:6px; background:$primaryColor; color:white; font-size:13px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; &:hover { background:$primaryDark; } &:disabled { opacity:0.6; cursor:not-allowed; } }

/* Warning modal body */
.ai-warning-body { text-align:center; padding:8px 0; }
.ai-warning-icon { width:48px; height:48px; border-radius:$radiusFull; background:#fef3c7; color:#d97706; display:flex; align-items:center; justify-content:center; font-size:20px; margin:0 auto 12px; }
.ai-warning-text { font-size:13px; color:$gray700; line-height:1.5; margin:0 0 16px; strong { font-weight:700; } }
.ai-warning-actions { display:flex; justify-content:center; gap:8px; }
.ai-cancel-btn { padding:7px 16px; border:1px solid $gray200; border-radius:6px; background:white; color:$gray600; font-size:12.5px; font-weight:600; cursor:pointer; font-family:inherit; transition:all $transitionFast; &:hover { background:$gray50; } }
.ai-confirm-btn { padding:7px 16px; border:none; border-radius:6px; background:#d97706; color:white; font-size:12.5px; font-weight:600; cursor:pointer; font-family:inherit; transition:all $transitionFast; display:inline-flex; align-items:center; gap:5px; &:hover { background:#b45309; } &:disabled { opacity:0.6; cursor:not-allowed; } }

/* View link */
.ai-view-link { display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border:none; border-radius:6px; background:$primaryColor; color:white; font-size:12.5px; font-weight:600; text-decoration:none; transition:all $transitionFast; &:hover { background:$primaryDark; } }

@media(max-width:480px) { .ai-form-grid { grid-template-columns:1fr; } .ai-field:last-child { grid-column:auto; } }
</style>
